import React, { useEffect, useRef, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import ProductVariants from "../../product/component/ProductVariants";
import {
  applyVariantPricingFromFields,
  getQuoteVariantsWithRulesAsync,
  getVariantTotal,
} from "../../common/util";

function findOptionByName(options, name) {
  return (options || []).find((o) => o.name === name);
}

/**
 * True when pricing on the user's current select choices differs between two field snapshots
 * (same option names still available in both).
 */
export function selectionsPricingChanged(prevFields, nextFields, variant) {
  if (!prevFields || !nextFields || !variant) return false;
  let changed = false;
  for (const key of Object.keys(variant)) {
    if (key === "size") continue;
    const pf = prevFields[key];
    const nf = nextFields[key];
    if (!pf?.field || pf.field.type !== "select" || !nf?.field || nf.field.type !== "select") {
      continue;
    }
    const v = variant[key];
    if (!v) continue;
    const prevOpts = pf.field.options || [];
    const nextOpts = nf.field.options || [];
    const isMulti = +pf.field.max > 1;
    const parts = v.value?.parts;

    if (parts != null) {
      for (const k of Object.keys(parts)) {
        const part = parts[k];
        if (!part?.name) continue;
        const po = findOptionByName(prevOpts, part.name);
        const no = findOptionByName(nextOpts, part.name);
        if (po && no && JSON.stringify(po.pricing || null) !== JSON.stringify(no.pricing || null)) {
          changed = true;
          break;
        }
      }
    } else if (isMulti && Array.isArray(v.value)) {
      for (const sel of v.value) {
        const po = findOptionByName(prevOpts, sel.name);
        const no = findOptionByName(nextOpts, sel.name);
        if (po && no && JSON.stringify(po.pricing || null) !== JSON.stringify(no.pricing || null)) {
          changed = true;
          break;
        }
      }
    } else {
      const name = v.value?.name ?? v.name;
      if (!name) continue;
      const po = findOptionByName(prevOpts, name);
      const no = findOptionByName(nextOpts, name);
      if (po && no && JSON.stringify(po.pricing || null) !== JSON.stringify(no.pricing || null)) {
        changed = true;
      }
    }
  }
  return changed;
}

/**
 * Removes select values that are no longer present in `fields` (e.g. after rules hide options).
 * Returns `{ variant, adjusted }` where `adjusted` means an existing selection was removed or trimmed.
 * For flavor/filling with `value.parts`, any invalid part clears both fields (user re-selects via FlavorSelection).
 */
export function sanitizeVariantAgainstFields(variant, fields, basePrice) {
  if (!variant || !fields) return { variant, adjusted: false };
  let next = { ...variant };
  let adjusted = false;

  for (const [key, fieldConfig] of Object.entries(fields)) {
    if (key === "size") continue;
    if (fieldConfig.field?.type !== "select") continue;
    const v = next[key];
    if (!v) continue;
    const options = fieldConfig.field.options || [];
    const names = new Set(options.map((o) => o.name));
    const isMulti = +fieldConfig.field?.max > 1;
    const parts = v.value?.parts;

    if (parts != null) {
      const hasInvalidPart = Object.keys(parts).some(
        (k) => parts[k]?.name && !names.has(parts[k].name)
      );
      if (hasInvalidPart) {
        adjusted = true;
        if (key === "flavor" || key === "filling") {
          const { flavor: _f, filling: _fi, ...rest } = next;
          next = rest;
        } else {
          const { [key]: _removed, ...rest } = next;
          next = rest;
        }
      }
    } else if (isMulti && Array.isArray(v.value)) {
      const vals = v.value;
      const filtered = vals.filter((o) => o && names.has(o.name));
      if (filtered.length !== vals.length) {
        adjusted = true;
        if (filtered.length === 0) {
          const { [key]: _removed, ...rest } = next;
          next = rest;
        } else {
          next = {
            ...next,
            [key]: {
              ...v,
              name: filtered.map((o) => o.name).join(", "),
              pricing: { amount: getVariantTotal(filtered, basePrice) },
              value: filtered,
            },
          };
        }
      }
    } else {
      const name = v.value?.name ?? v.name;
      if (name && !names.has(name)) {
        adjusted = true;
        const { [key]: _removed, ...rest } = next;
        next = rest;
      }
    }
  }
  return { variant: adjusted ? next : variant, adjusted };
}

/**
 * Derives `fields` from `item.variants` + `item.rules` + `item.variant` when `item.rules` is non-empty.
 */
export default function QuoteLineItemVariants({
  item,
  basePrice,
  onVariantChange,
  onDerivedFields,
}) {
  const [fields, setFields] = useState(item.variants);
  const [toast, setToast] = useState({ open: false, message: "" });
  const onVariantChangeRef = useRef(onVariantChange);
  onVariantChangeRef.current = onVariantChange;
  const onDerivedFieldsRef = useRef(onDerivedFields);
  onDerivedFieldsRef.current = onDerivedFields;
  const prevFieldsRef = useRef(null);
  const hasCompletedRulesRunRef = useRef(false);
  const lineIdentityRef = useRef("");

  useEffect(() => {
    const id = `${item.productId ?? ""}:${item.title ?? ""}`;
    if (lineIdentityRef.current && lineIdentityRef.current !== id) {
      hasCompletedRulesRunRef.current = false;
      prevFieldsRef.current = null;
    }
    lineIdentityRef.current = id;
  }, [item.productId, item.title]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const next = await getQuoteVariantsWithRulesAsync({
        variants: item.variants,
        variant: item.variant,
        rules: item.rules,
      });
      if (cancelled) return;
      setFields(next);
      onDerivedFieldsRef.current?.(next);

      const { variant: sanitized, adjusted } = sanitizeVariantAgainstFields(
        item.variant,
        next,
        basePrice
      );
      const { variant: finalVariant, changed: pricingSynced } = applyVariantPricingFromFields(
        sanitized,
        next,
        basePrice
      );

      if (!hasCompletedRulesRunRef.current) {
        hasCompletedRulesRunRef.current = true;
        prevFieldsRef.current = next;
        if (JSON.stringify(finalVariant) !== JSON.stringify(item.variant)) {
          onVariantChangeRef.current(finalVariant);
        }
        return;
      }

      const pricingChanged = selectionsPricingChanged(prevFieldsRef.current, next, item.variant);

      if (adjusted || pricingChanged || pricingSynced) {
        setToast({
          open: true,
          message:
            "Heads up! Your new selection has updated some of your previous choices and pricing. Take a quick look to make sure everything looks good!",
        });
      }

      prevFieldsRef.current = next;

      if (JSON.stringify(finalVariant) !== JSON.stringify(item.variant)) {
        onVariantChangeRef.current(finalVariant);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [item.variant, item.variants, item.rules, basePrice]);

  return (
    <>
      <ProductVariants
        basePrice={basePrice}
        variant={item.variant}
        fields={fields}
        onChange={onVariantChange}
      />
      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
